import * as React from "react";

import "./street-map-section.scss";

import {UseState}   from "@Common/react/use-state";
import {cs}         from "@Common/react/chain-services";
import {BubbleNoti} from "@Common/ui-components/bubble-noti/bubble-noti";

import {NeighborhoodMapControl} from "./neighborhood-map-control/neighborhood-map-control";
import {InformationBadge}       from "heavy/common/design2/information-badge/information-badge";
import {formatNumberBig}        from "../../../../../../../language/format-number-big";
import {cx}               from "emotion";

export const StreetMapSection = ({routes, selecteds, openPoBoxTab, viewSize, next: rootNext}) => cs(
    ["hoveredShape", (_, next) => UseState({
        next,
    })],
    ["disabled", (_, next) => UseState({initValue: false, next})],
    ["noti", (_, next) => BubbleNoti({next})],
    ({hoveredShape, disabled, noti}) => {
        const {selectedAddressesCount, selectedRoutesCount} = !routes ? {selectedAddressesCount: 0, selectedRoutesCount: 0} : (
            routes.reduce((counts, route) => {
                if (selecteds.value?.[toKey(route)]) {
                    counts.selectedAddressesCount += route.count;
                    counts.selectedRoutesCount++;
                }
                return counts;
            }, {selectedAddressesCount: 0, selectedRoutesCount: 0})
        );
        const addressCounts = [
            {
                label: "Selected Neighborhoods",
                count: selectedRoutesCount,
            },
            {
                label: "Available Neighborhoods",
                count: routes?.length,
            },
            {
                label: "Selected Addresses",
                count: selectedAddressesCount,
            },
            {
                label: "Available Addresses",
                count: routes?.reduce((count, route) => (
                    count += route.count
                ), 0),
            },
        ];
        const routeControl = routes?.filter(r => r.route_type !== "PO Boxes").some(r => !selecteds.value?.[toKey(r)]) ? {
            buttonType: "select",
            label: "Select All Mail Routes",
            onClick: () => selecteds.change((_) => ({..._, ...routes?.filter(r => r.route_type !== "PO Boxes")?.reduce((s, r) => ({...s, [toKey(r)]: true}), {})})),
        } : {
            buttonType: "deselect",
            label: "Deselect All Mail Routes",
            onClick: () => selecteds.change((_) => ({..._, ...routes?.filter(r => r.route_type !== "PO Boxes")?.reduce((s, r) => ({...s, [toKey(r)]: false}), {})})),
        };
        const includesPoBoxes = routes?.some(r => r.route_type === "PO Boxes");

        // Add post office feature once PO Box routes properly report lat/lng of Post Office
        // ADD LAT LNG TO NEIGHBORHOOD POLYGONS GLOBAL STATE
        // const postOffices = routes?.filter(r => r.route_type === "PO Boxes").reduce((postOffices, route) => ([
        //     ...postOffices,
        //     !postOffices.some(office => office.elementKey === ""+route.lat+route.lng) && {
        //         elementKey: ""+route.lat+route.lng,
        //         coords: {
        //             lat: route.lat,
        //             lng: route.lng,
        //         },
        //     },
        // ].filter(v=>v)), []);
        
        return rootNext({
            disabled,
            getUnselecteds: () => routes.filter(r => !selecteds.value?.[toKey(r)]).map((route) => ({
                route: route.route,
                zip_code: route.zip_code,
            })),
            renderErrorNoti: ({message, color, duration}) => noti.show({
                message,
                color,
                duration,
            }),
            render: () => (
                <div className={cx("street-map-section-3f5", {disabled: disabled.value})}>
                    {!disabled.value && routes && (
                        <div className="map-overlay">
                            <div className="right">
                                <div className="address-count-box">
                                    <div className="header">
                                        <div className="title" xd-qa-name="Neighborhood Search Select Neighborhoods" xd-qa-type="label">
                                            Select Neighborhoods
                                            {includesPoBoxes && <span className="tooltip">
                                                {InformationBadge({
                                                    getTooltip: () => (
                                                        <div className="message-5ww">
                                                            <p>These counts include PO Box neighborhood routes.</p>
                                                            <p>PO Box neighborhood routes are not pictured in the map.</p>
                                                        </div>
                                                    ),
                                                    direction: "south-west",
                                                    xdQaName: "Neighborhood Search Info Badge",
                                                })}
                                            </span>}
                                        </div>
                                    </div>
                                    {addressCounts?.map((count, i) => (
                                        <div className="line" key={i}>
                                            <div 
                                                className="label"
                                                xd-qa-name={`Neighborhood Search ${count.label}`}
                                                xd-qa-type="label"
                                            >{count.label}:</div>
                                            <div
                                                className="count"
                                                xd-qa-name={`Neighborhood Search ${count.label} Count`}
                                                xd-qa-type="label"
                                            >{formatNumberBig(count.count)}</div>
                                        </div>
                                    ))}
                                    {includesPoBoxes && (
                                        <a {...{
                                            className: "po-boxes",
                                            onClick: openPoBoxTab,
                                            "xd-qa-name": "Neighborhood Search Select PO Box Neighborhoods",
                                            "xd-qa-type": "link",
                                        }}>
                                            Select PO Box Neighborhood Routes ({routes.filter(r => selecteds.value?.[toKey(r)] && r.route_type === "PO Boxes").length})
                                        </a>
                                    )}  
                                </div>
                                <button 
                                    className={"route-control " + routeControl.buttonType}
                                    onClick={routeControl.onClick}
                                    xd-qa-name="Neighborhood Search Select All Mail Routes"
                                    xd-qa-type="button"
                                >
                                        {routeControl.label}
                                </button>
                            </div>
                        </div>
                    )}

                    {NeighborhoodMapControl({
                        center: [37, -111],
                        hoveredShape,
                        shapes: routes?.filter(r => r.route_type !== "PO Boxes" && r.polygon)?.map((route) => ({
                            name: route.route,
                            elementKey: toKey(route),
                            count: route.count,
                            vertexList: route.polygon,
                            color: route.match_rate < 30 || route.match_rate === undefined ? 'red' : route.match_rate < 70 ? 'orange' : 'green',
                            opacity: (!selecteds.value?.[toKey(route)] ? .2 : .6) + (hoveredShape.value?.key === toKey(route) && .2),
                            onClick: () => selecteds.change((_) => ({..._,[toKey(route)]: !_?.[toKey(route)]})),
                            onMouseOver: () => hoveredShape.onChange({key: toKey(route), type: "polygon", name: route.zip_code + '-' + route.route, count: route.count, match_rate: route.match_rate}),
                            onMouseOut : () => hoveredShape.onChange(null),
                        })),
                        // Add post office feature once PO Box routes properly report lat/lng of Post Office
                        // postOffices: postOffices?.map(office => ({
                        //     ...office,
                        //     onClick: openPoBoxTab,
                        //     onMouseOver: () => hoveredShape.onChange({key: office.elementKey, type: "office"}),
                        //     onMouseOut : () => hoveredShape.onChange(null),
                        // })),
                        viewSize,
                    })}
                </div>
            )
        })
    },
);

const toKey = (route) => route.zip_code + route.route